import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AdminClient } from "../../api/generated/APIClient";
import { useAuth } from "../../contexts/AuthContext";
import "./Sidebar.css";

// 簡易NavItem型定義
interface NavItem {
  id: string;
  label: string;
  path?: string;
  icon?: string;
  children?: NavItem[];
  isExpanded?: boolean;
  badge?: number; // バッジ表示用（未読数など）
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();

  // ローカルストレージのキー
  const SIDEBAR_STATE_KEY = "sidebar-expanded-items";

  // ローカルストレージから展開状態を読み込む
  const loadExpandedState = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.warn("サイドバー状態の読み込みに失敗:", error);
      return {};
    }
  };

  // ローカルストレージに展開状態を保存する
  const saveExpandedState = (expandedState: Record<string, boolean>) => {
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(expandedState));
    } catch (error) {
      console.warn("サイドバー状態の保存に失敗:", error);
    }
  };

  // 未読お問い合わせ数（お問い合わせを開閉するたびに変わるため、画面遷移ごとに取り直す）
  const [unreadContactCount, setUnreadContactCount] = useState(0);
  const { user } = useAuth();
  const isServerAdmin = user?.role === "server_admin";

  useEffect(() => {
    let cancelled = false;

    const fetchUnreadCount = async () => {
      try {
        const response = await AdminClient.GetUnreadContactCount({});
        if (cancelled) return;
        const count = Number((response as any)?.data?.unreadCount ?? 0);
        setUnreadContactCount(Number.isFinite(count) ? count : 0);
      } catch (error) {
        // 取得できなくてもサイドバー自体は使えるため、バッジを出さずに続行する
        console.warn("未読お問い合わせ数の取得に失敗:", error);
      }
    };

    void fetchUnreadCount();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // 初期ナビゲーションデータ
  const getInitialNavItems = useCallback((): NavItem[] => {
    return [
      {
        id: "contact",
        label: "お問い合わせ",
        path: "/admin",
        badge: unreadContactCount > 0 ? unreadContactCount : undefined,
      },
      {
        id: "result",
        label: "施工実績",
        path: "/admin/entities/result",
      },
      {
        id: "result-category",
        label: "施工実績カテゴリ",
        path: "/admin/entities/result-category",
      },
      {
        id: "maintenance",
        label: "メンテナンス",
        path: "/admin/entities/maintenance",
      },
      {
        id: "announce",
        label: "お知らせ",
        path: "/admin/entities/announce",
      },
      // アクセス解析（IP を含む）と開発者アカウントの管理はサーバー管理者だけ
      ...(isServerAdmin
        ? [
            {
              id: "analytics",
              label: "アクセス解析",
              path: "/admin/analytics",
            },
            {
              id: "admin-user",
              label: "管理ユーザー",
              path: "/admin/entities/admin-user",
            },
          ]
        : []),
    ];
  }, [unreadContactCount, isServerAdmin]);

  // 展開状態を適用する関数
  const applyExpandedState = (
    items: NavItem[],
    expandedState: Record<string, boolean>
  ): NavItem[] => {
    return items.map((item) => ({
      ...item,
      isOpen: expandedState[item.id] || false,
      children: item.children
        ? applyExpandedState(item.children, expandedState)
        : item.children,
    }));
  };

  // 展開状態の管理用
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    () => loadExpandedState()
  );

  // 動的にナビゲーション項目を生成（URLが変更されるたびに再計算）
  const navItems = useMemo(() => {
    const initialItems = getInitialNavItems();
    return applyExpandedState(initialItems, expandedItems);
  }, [getInitialNavItems, expandedItems]);

  const toggleNavItem = (itemId: string) => {
    const newExpandedState = {
      ...expandedItems,
      [itemId]: !expandedItems[itemId],
    };

    setExpandedItems(newExpandedState);
    saveExpandedState(newExpandedState);
  };

  const isActiveRoute = (path?: string): boolean => {
    if (!path) return false;

    // 完全一致の場合
    if (location.pathname === path) return true;

    // /adminの場合はプレフィックスマッチも許可（お問い合わせの詳細・編集ページ用）
    if (path === "/admin") {
      // /admin/entities/* は別のページなのでマッチしない
      if (location.pathname.startsWith("/admin/entities/")) return false;
      return location.pathname.startsWith("/admin/");
    }

    // それ以外はプレフィックスマッチ
    return location.pathname.startsWith(path + "/");
  };

  const renderNavItem = (item: NavItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = isActiveRoute(item.path);
    const isOpen = expandedItems[item.id] || false;

    return (
      <div key={item.id} className={`nav-item level-${level}`}>
        <div
          className={`nav-item-content ${isActive ? "active" : ""}`}
          onClick={() => hasChildren && toggleNavItem(item.id)}
        >
          {item.path ? (
            <Link to={item.path} className="nav-link">
              <span className="nav-label">{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className="nav-badge">{item.badge}</span>
              )}
            </Link>
          ) : (
            <>
              <span className="nav-label">{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className="nav-badge">{item.badge}</span>
              )}
            </>
          )}
          {hasChildren && (
            <button className={`nav-toggle ${isOpen ? "open" : ""}`}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M6.22 4.22a.75.75 0 0 1 1.06 0L10.56 7.5a.75.75 0 0 1 0 1.06L7.28 11.78a.75.75 0 1 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z" />
              </svg>
            </button>
          )}
        </div>
        {hasChildren && isOpen && (
          <div className="nav-children">
            {item.children!.map((child) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        className="sidebar-toggle"
        onClick={onToggle}
        aria-label="サイドバーを開閉"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
        </svg>
      </button>

      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <nav className="sidebar-nav">
          {navItems.map((item) => renderNavItem(item))}
        </nav>
      </div>

      {isOpen && <div className="sidebar-overlay" onClick={onToggle} />}
    </>
  );
};
